import { AxiosResponse } from 'axios'

export type TryAxiosResponse<T> = AxiosResponse<T | ServerErrorResponse> | undefined

// based on CoreLib.Exceptions.CoreApiError
export type ServerErrorResponse = {
  Message: string
  ExceptionMessage?: string
  ExceptionType?: string
  InnerException?: string
  MessageDetail?: string
  StackTrace?: string
  StatusCode?: number
  StatusMessage?: string
  Url?: string
  User?: string
}

// could incorrectly assert an ErrorInterface is a ServerErrorResponse

export function isServerErrorResponse(value: any): value is ServerErrorResponse {
  // assuming this is a good test
  return !!value?.Message && (!!value.ExceptionMessage || !!value?.StackTrace || !!value?.StatusCode)
}

export interface IProfileService {
  deleteStudents(stuId: string): Promise<JustMessage | ServerErrorResponse>
  get365Apps(): Promise<O365AppResponse | ServerErrorResponse>
  getContactInfo(): Promise<UserContactInfo | ServerErrorResponse>
  getDeviceLogs(logUrl: string): Promise<DeviceLog[] | ServerErrorResponse>
  getMyDevices(): Promise<IDevice[] | ServerErrorResponse>
  getMyStudentDevices(): Promise<IStudentWithDevices[] | ServerErrorResponse>
  getPasswordPolicy(): Promise<string | ServerErrorResponse>
  getPhoneHelp(): Promise<string | ServerErrorResponse>
  getStatesInfo(): Promise<unknown | ServerErrorResponse>
  getStudentAddRequests(): Promise<unknown[] | ServerErrorResponse>
  getStudentAssignments(stuId: string, courseId: string): Promise<StudentAssignment[] | ServerErrorResponse>
  getStudentAttendance(periodId: string, stuId: string): Promise<StudentAttendance[] | ServerErrorResponse>
  getStudentContactInfo(studentId: string): Promise<StudentContactInfo[] | ServerErrorResponse>
  getStudentGrades(): Promise<unknown | ServerErrorResponse>
  getStudentInfo(): Promise<StudentInfo[] | ServerErrorResponse>
  getStudents(): Promise<Student[] | ServerErrorResponse>
  getUserProfileInfo(): Promise<UserInterface | ServerErrorResponse>
  getUserSchools(): Promise<School[] | ServerErrorResponse>
  getBusStops(studentId: string): Promise<BusStopInfo[] | ServerErrorResponse>
  postCheckPassword(passwordEntered: string): Promise<CheckPasswordResponse | ServerErrorResponse>
  postContactInfo(userData: Partial<UserContactInfo>): Promise<UserContactInfo | ServerErrorResponse>
  postEmailRename(email: string): Promise<unknown | ServerErrorResponse>
  postStudents(
    studentIDPassed: string,
    studentDobPassed: string,
    studentLastName: string
  ): Promise<JustMessage | ServerErrorResponse>
  postVerifyPhoneNumber(id: string, code: string): Promise<JustMessage | ServerErrorResponse>
  saveNewPassword(currentPassword: string, newPassword: string): Promise<JustMessage | ServerErrorResponse>
  verifyAvailableEmail(email: string): Promise<VerifyAvailableEmailResponse | ServerErrorResponse>
  verifyPhoneNumber(): Promise<VerifyPhoneNumberResponse | ServerErrorResponse>
  getProfileSettings(): Promise<any | ServerErrorResponse>
  updateProfileSettings(settings: any): Promise<any | ServerErrorResponse>
}

export interface JustMessage {
  Message: string
}

export interface DriverLicenseResponse {
  Id: string
  Timestamp: string // ISO date string when the license was uploaded
  Size: number // File size in bytes
  FileName: string
  MimeType: string // e.g., "image/png", "image/jpeg"
  UPN: string
  DisplayName: string
  Email: string
  ImageBytes: string // Base64 encoded image data (raw base64, no data URL prefix)
}

// Response for check-only endpoint (without ImageBytes)
export interface DriverLicenseCheckResponse {
  Id: string
  Timestamp: string // ISO date string when the license was uploaded
  Size: number // File size in bytes
  FileName: string
  MimeType: string // e.g., "image/png", "image/jpeg"
  UPN: string
  DisplayName: string
  Email: string
}

export interface DeviceLog {
  onPrem: boolean
  deviceName: string
  createdDateTime: any
  city: string
  state: string
  latitude: any
  longitude: any
}
export interface VerifyPhoneNumberResponse {
  Message: string
  ResponseId: string
}

export interface O365App {
  Title: string
  Url: string
  Icon: string
}

export interface O365AppResponse {
  Apps: O365App[]
}

export interface UserInterface {
  AccountType: string
  CanChangePassword: boolean
  CanEditProfile: boolean
  DisplayName: any // may always be string
  Email: any // may always be string
  ExternalPasswordChangeUrl?: string
  ExternalProfileUrl?: string
  FirstName: string
  HasDevices: boolean
  HasExternalPasswordChangeUrl: boolean
  HasExternalProfileUrl: boolean
  HasJobDescription: boolean
  HasStudentContactInfo: boolean
  HasStudentDevices: boolean
  HasStudentInfo: boolean
  IsImperator: boolean
  JobDescription: string
  LastName: string
  MyDeviceCount: number
  ObjectID: string // may always be guid string
  PhotoUrl: any // may always be string - url
  StudentContactCount: number
  StudentCount: number
  StudentDeviceCount: number
  StudentIds: any // may always be ?[]
  UserPrincipalName: string
  dcpsAUPAcceptanceDate: string
  dcpsID: string
  dcpsType: string
  extranetGUID: string
}

export type Guid = string

export type IStudentWithDevices = {
  AssignedDevices: IDevice[]
  DateOfBirth: string
  DisplayName: string
  FirstName: string
  GradeLevel: string
  Id: string
  LastName: string
  PhotoUrl: string
  UserPrincipalName: string
  extranetGUID: string
  student_id: number
}

export type ITicket = {
  AssetId: string
  Assigned_Group: string
  Assigned_Support_Organization: string
  Description: string
  Incident_Number: string
  Last_Resolved_Date: string
  Resolution: string | null
  Status: string
  SubmitDate: string
  Submitter: string
}
export type IDevice = {
  Id: Guid
  Model: string
  Manufacturer: string
  SerialNumber: string
  Region: string
  SiteGroup: string
  Site: string
  Item: string
  Name: string
  Type: string
  DeviceName: string
  HasLogs: boolean
  AssetId: string
  Classification: string
  Description: string
  PurchaseCost: number
  MarketValue: number
  ESports: boolean
  Title1: boolean
  CareerTechEdu: boolean
  Spare: boolean
  ECF: boolean
  Lab: boolean
  AssetLifecycleStatus: string
  StatusReason: string
  SapStatus: string
  LastScanDate: string
  LoginName: string
  dcpsType: string
  dcpsID: string
  IsComputer: boolean
  IsStudentDevice: boolean
  DeviceLogonsUrl: string
  Tickets?: ITicket[]
}
export type StateInfo = {
  Abbreviation: string
  Name: string
}
export type UserContactInfo = {
  Address: string
  ApartmentNumber: string
  City: string
  EmailAddress: string
  FirstName: string
  IsMobilePhone: boolean
  LastName: string
  MobilePhone: string
  PostalCode: string
  SecondaryEmailAddress?: string
  State: string
  MobilePhoneInfo: { IsVerified: boolean }
}

// Update type to represent both school and regular stop locations
export type StopLocation = {
  Type: 'School' | 'Stop' // Now can be either "School" or "Stop"
  Number?: string // Only for schools
  Key: string // Present for both types
  Name: string // Present for both types
  Address?: string // Only for schools
  City?: string // Only for schools
  Zip?: string // Only for schools
  State?: string // Only for schools
  LastLookup: string // Timestamp of when the coordinates were looked up
  HasCoordinateError: boolean
  DisplayAddress: string // Formatted address for display
  Coordinates: [number, number] // Now consistently provided for all stop types
  HasCoordinates: boolean // Flag to indicate if coordinates are available
  StartTime?: string // School start time (format: "HH:MM AM/PM")
  EndTime?: string // School end time (format: "HH:MM AM/PM")
}

export type BusStopRaw = {
  student_id: string
  'Transportation Eligible': string
  'School Distance': number
  'Itinerary Effective Date': string
  'Route 1': string
  'Non-Rider 1': unknown
  'Run Note 1': string
  'Stop Location 1': string
  'Time 1': string // '07:40 AM',
  'Route 2': string
  'Non-Rider 2': unknown
  'Run Note 2': string
  'Stop Location 2': string
  'Time 2': string
  'Route 3': string
  'Non-Rider 3': unknown
  'Run Note 3': string
  'Stop Location 3': string
  'Time 3': string
  'Route 4': string
  'Non-Rider 4': unknown
  'Run Note 4': string
  'Stop Location 4': string
  'Time 4': string
  'School Stop Location 1': StopLocation | null
  'School Stop Location 2': StopLocation | null
  'School Stop Location 3': StopLocation | null
  'School Stop Location 4': StopLocation | null
  Student?: StudentSchoolInfo | null
}

export type StudentSchoolInfo = {
  Id: number
  FirstName: string
  LastName: string
  SchoolKey: string
  School: StopLocation
  CurrentSchoolYear?: number
  NextSchoolYear?: number
  HasNextYear?: boolean
}

export type BusStopRoute = {
  route: string
  nonRider: unknown
  runNote: string
  location: string
  time: string
  stopLocation?: StopLocation // Renamed from schoolLocation to stopLocation since it can be any stop type
  coordinates?: [number, number]
  addressDetails?: string
  stopType?: 'School' | 'Stop' // Add an explicit stop type field for easier filtering
}

export function getRoute(i: 1 | 2 | 3 | 4, bsi: BusStopRaw): BusStopRoute {
  let routeKey = `Route ${i}` as 'Route 1' | 'Route 2' | 'Route 3' | 'Route 4'
  let nonRiderKey = `Non-Rider ${i}` as 'Non-Rider 1' | 'Non-Rider 2' | 'Non-Rider 3' | 'Non-Rider 4'
  let runNoteKey = `Run Note ${i}` as 'Run Note 1' | 'Run Note 2' | 'Run Note 3' | 'Run Note 4'
  let locationKey = `Stop Location ${i}` as
    | 'Stop Location 1'
    | 'Stop Location 2'
    | 'Stop Location 3'
    | 'Stop Location 4'
  let timeKey = `Time ${i}` as 'Time 1' | 'Time 2' | 'Time 3' | 'Time 4'
  let schoolLocationKey = `School Stop Location ${i}` as
    | 'School Stop Location 1'
    | 'School Stop Location 2'
    | 'School Stop Location 3'
    | 'School Stop Location 4'

  // Get the stop location data if available
  const stopLocation = bsi[schoolLocationKey] || null

  // Extract coordinates, address details and stop type if location is available
  let coordinates: [number, number] | undefined = undefined
  let addressDetails: string | undefined = undefined
  let stopType: 'School' | 'Stop' | undefined = undefined

  if (stopLocation && stopLocation.HasCoordinates) {
    coordinates = stopLocation.Coordinates
    addressDetails = stopLocation.DisplayAddress
    stopType = stopLocation.Type as 'School' | 'Stop'
  }

  return {
    route: bsi[routeKey] || '',
    nonRider: bsi[nonRiderKey] || '',
    runNote: bsi[runNoteKey] || '',
    location: bsi[locationKey] || '',
    time: bsi[timeKey] || '',
    stopLocation: stopLocation || undefined,
    coordinates,
    addressDetails,
    stopType,
  }
}

export function getRoutes(bsi: BusStopRaw): BusStopRoute[] {
  // Create an array to hold all valid routes
  const routes: BusStopRoute[] = []

  // Check each route individually - only add if it has both a route number and location
  if (bsi['Route 1'] && bsi['Stop Location 1']) routes.push(getRoute(1, bsi))
  if (bsi['Route 2'] && bsi['Stop Location 2']) routes.push(getRoute(2, bsi))
  if (bsi['Route 3'] && bsi['Stop Location 3']) routes.push(getRoute(3, bsi))
  if (bsi['Route 4'] && bsi['Stop Location 4']) routes.push(getRoute(4, bsi))

  return routes
}

export type BusStopInfo = {
  studentId: string
  transportationEligible: string
  schoolDistance: number
  itineraryEffectiveDate: string
  routeStops: BusStopRoute[]
  assignedSchool?: StopLocation // Student's assigned school from Student.School
  Student?: StudentSchoolInfo | null // Add the Student property to match BusStopRaw
  CurrentSchoolYear?: number
}

export type CheckPasswordResponse = {
  Valid: boolean
  Password: string
  Violations: string[]
}
export type VerifyAvailableEmailResponse = {
  Accepted: boolean
  Message: string
  Email: string
}

// fetched from /api/profile/students via webAPIEndPointStudents
export type Student = {
  Id: string
  PhotoUrl: string
  DisplayName: string
  GradeLevel: string
  DateOfBirth: string
}

export type StudentAssignment = {
  assignment_title: string
  description: string
  due_date: string
  publish_date: string
  no_late_turnin: string // cspell:disable-line
  letter_grade: string
  points: string
  comments: string
}

export type StudentAttendance = {
  title: string
  period: string
  school_date: string
}

export type StudentInfoGrade = {
  start_date: string // like "2023-08-14T00:00:00"
  end_date: string
  period_id: string
  student_id: string
  course_period_id: string
  period: string
  course_title: string
  teacher: string
  percent_grade: number
  credits: number
  gpa_points: number
  grade_title: string
  attendance_item_count: number
  assignment_count: number
}

// fetched from /api/profile/myStudentInfo
// does not perfectly reflect Student type
// commenting out things we aren't using even if they are on the data currently
export type StudentInfo = {
  Id: string
  DisplayName: string
  GradeLevel: string
  // FirstName: string
  // LastName: string
  PhotoUrl: string
  DateOfBirth: string
  CurrentGrades: StudentInfoGrade[]
  CurrentSchoolYear?: number
}

export type StudentContactInfoInfo = {
  id: string
  title: string
  value: any
}

export type StudentContactInfoContact = {
  person_id: string
  first_name: string
  last_name: string
  student_relation?: string
  email: string
  emergency?: string
  custody?: string
  contact_info: StudentContactInfoInfo[]
  address?: { address: string; city: string; state: string; zipcode: string } // cspell:ignore: zipcode
}

export type StudentContactInfo = {
  contacts: StudentContactInfoContact[]
}

export type School = {
  SortedRole: string
}
